<template>
    <div>
        <div class="row b-b">
            <div class="col-12 p-3">
                <company-form-token></company-form-token>
            </div>
        </div>
        <div class="row">
            <div class="col-12 p-3">
                <info-alert-box alert-type="warning"
                                text="Please note that the SMS API usage is considered automated & paid. You are agreeing to our billing terms by using the API."
                                kb-link="https://support.aloware.com/en/articles/9033804-aloware-pricing-plans-empowering-your-teams-with-flexible-solutions">
                </info-alert-box>

                <div class="mb-2">
                    <strong class="integration-api-titles">Send an SMS to a phone number.</strong>
                </div>
                <p class="text-md">
                    <span class="text-dark-greenish _600">POST /api/v1/webhook/sms-gateway/send</span>
                </p>
                <p>
                    <strong>Required Fields:</strong>
                    <br>
                    <span class="text-dark-greenish _600">api_token</span>,
                    <span class="text-dark-greenish _600">from or line_id</span>,
                    <span class="text-dark-greenish _600">to</span>,
                    <span class="text-dark-greenish _600">message</span>
                </p>

                <p>
                    <strong>Optional Fields:</strong>
                    <br>
                    <span class="text-dark-greenish _600">image_url (to send mms)</span>
                    <br>
                    <span class="text-dark-greenish _600">force_random (to ignore number stickiness can be 0 or 1)</span>
                    <br>
                    <span class="text-dark-greenish _600">custom_fields (add up to three fields that are important to you)</span>
                    <br>
                    <span class="text-dark-greenish _600">user_id (send the message on behalf of a user. Can be -1, 0 or a user id)
                        <br>
                        <ul>
                            <li>
                                If sending -1, the message will be sent as the company with no user ownership.
                            </li>
                            <li>
                                If not sending the user_id field or sending 0, the message will be sent as the contact owner. If the contact has no owner, it will be sent as the company.
                            </li>
                            <li>
                                If sending any user id, the message will be sent as the user. If the provided user id does not exist, it will return a 422 error.
                            </li>
                        </ul>
                    </span>
                </p>

                <p>
                    <strong>Example POST URL:</strong>
                    <br>
                    <span class="text-dark-greenish _600">{{ statics.domain | fixDomain }}/api/v1/webhook/sms-gateway/send</span>
                </p>

                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        The code sample below represents some example JSON with
                        standard fields to pass in the body of your request in
                        order to send an SMS:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_a'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001", // put one of your line's phone numbers here
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an SMS from Aloware." // Limit to 160 characters.
}</code></pre>

                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        The code sample below represents some example JSON with
                        standard fields to pass in the body of your request in
                        order to send an SMS with User ID:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_b'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001", // put one of your line's phone numbers here
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an SMS from Aloware.", // Limit to 160 characters.
    "user_id": 1234 // Aloware User ID to send SMS on behalf of.
}</code></pre>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_c'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001", // put one of your line's phone numbers here
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an SMS from Aloware.", // Limit to 160 characters.
    "user_id": -1 // Send as the company with no user ownership.
}</code></pre>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_d'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001", // put one of your line's phone numbers here
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an SMS from Aloware.", // Limit to 160 characters.
    "user_id": 0 // Send as the contact owner. If the contact has no owner, it will be sent as the company.
}</code></pre>

                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        The code sample below represents some example JSON with
                        standard fields to pass in the body of your request in
                        order to send an SMS with Line ID:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_e'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "line_id": 1234, // Aloware Line ID to send SMS from.
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an SMS from Aloware." // Limit to 160 characters.
}</code></pre>

                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        The code sample below represents some example JSON with
                        standard fields to pass in the body of your request in
                        order to send an MMS:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_f'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001", // put one of your line's phone numbers here
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an MMS from Aloware.", // Limit to 160 characters.
    "image_url": "https://placekitten.com/408/287"
}</code></pre>

                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        The code sample below represents some example JSON with
                        standard fields to pass in the body of your request in
                        order to send an MMS with User ID:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_g'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001", // put one of your line's phone numbers here
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an MMS from Aloware.", // Limit to 160 characters.
    "image_url": "https://placekitten.com/408/287",
    "user_id": 1234 // Aloware User ID to send MMS on behalf of.
}</code></pre>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_h'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001", // put one of your line's phone numbers here
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an MMS from Aloware.", // Limit to 160 characters.
    "image_url": "https://placekitten.com/408/287",
    "user_id": -1 // Send as the company with no user ownership.
}</code></pre>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_i'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001", // put one of your line's phone numbers here
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an MMS from Aloware.", // Limit to 160 characters.
    "image_url": "https://placekitten.com/408/287",
    "user_id": 0 // Send as the contact owner. If the contact has no owner, it will be sent as the company.
}</code></pre>
                <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        The code sample below represents some example JSON with
                        standard fields to pass in the body of your request in
                        order to send an MMS with Line ID:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_j'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "line_id": 1234, // Aloware Line ID to send SMS from.
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an MMS from Aloware.", // Limit to 160 characters.
    "image_url": "https://placekitten.com/408/287"
}</code></pre>
              <p class="mb-2">
                    <strong>Example POST body:</strong>
                    <br>
                    <span class="text-muted">
                        The code sample below represents some example JSON with
                        standard fields to pass in the body of your request in
                        order to send an SMS with custom fields:
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_k'"><code class="json">{
    "api_token": "{{ companyToken }}",
    "line_id": 1234, // Aloware Line ID to send SMS from.
    "to": "+18181234567", // put your lead’s phone number here
    "message": "Hello, an SMS from Aloware with Custom Fields.",
    "custom_fields": { // up to 3 custom fields
        "custom_field_1": "value1", // The key and value (string) of the custom field, it will be shown as Custom Field 1: value1.
        "custom_field_2": "value2", // The key and value (string) of the custom field, it will be shown as Custom Field 2: value2.
        "custom_field_3": "value3"  // The key and value (string) of the custom field, it will be shown as Custom Field 3: value3.
    }
}</code></pre>

                <p class="mb-2">
                    <strong>Example POST header:</strong>
                </p>
                <pre v-highlightjs><code class="json">{
    "Accept": "application/json",
    "Content-Type": "application/json"
}</code></pre>

                <p class="mb-2">
                    <strong>Returns an HTTP 202 response on sms sent, Example success
                        response:</strong>
                </p>
                <pre v-highlightjs><code class="json">{
    "message": "Message sent."
}</code></pre>

                <p class="mb-2">
                    <strong>Returns an HTTP 422, 401 or 400 on failure with a detailed response, Example
                        failure
                        response:</strong>
                </p>
                <pre v-highlightjs><code class="json">{
    "message": "The given data was invalid.",
    "errors": {
        "message": [
            "The message field is required."
        ]
    }
}</code></pre>

                <p class="mb-2">
                    <strong>cURL Example:</strong>
                    <span class="text-muted">
                        With phone number.
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_l'" v-if="!loading_whitelabel"><code class="bash">curl -X POST \
  {{ statics.domain | fixDomain }}/api/v1/webhook/sms-gateway/send \
  -H 'Accept: application/json' \
  -H 'Content-Type: application/json' \
  -d '{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001",
    "to": "+18181234567",
    "message": "Hello, a SMS from Aloware."
  }'</code></pre>

                <p class="mb-2">
                    <strong>cURL Example:</strong>
                    <span class="text-muted">
                        With User ID.
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_m'" v-if="!loading_whitelabel"><code class="bash">curl -X POST \
  {{ statics.domain | fixDomain }}/api/v1/webhook/sms-gateway/send \
  -H 'Accept: application/json' \
  -H 'Content-Type: application/json' \
  -d '{
    "api_token": "{{ companyToken }}",
    "from": "+18552562001",
    "user_id": 1234, // Aloware USER ID to send SMS on behalf of.
    "to": "+18181234567",
    "message": "Hello, a SMS from Aloware."
  }'</code></pre>

                <p class="mb-2">
                    <strong>cURL Example:</strong>
                    <span class="text-muted">
                        With Line ID.
                    </span>
                </p>
                <pre v-highlightjs :key="renderRefreshToken + '_sms_n'" v-if="!loading_whitelabel"><code class="bash">curl -X POST \
  {{ statics.domain | fixDomain }}/api/v1/webhook/sms-gateway/send \
  -H 'Accept: application/json' \
  -H 'Content-Type: application/json' \
  -d '{
    "api_token": "{{ companyToken }}",
    "line_id": 1234, // Aloware Line ID to send SMS from.
    "to": "+18181234567",
    "message": "Hello, a SMS from Aloware."
  }'</code></pre>

                <p class="_600"
                   v-if="!statics.whitelabel">
                    If you need more API functions, please contact our support at support@aloware.com .
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import {company_token_mixin} from '../mixins'
import CompanyFormToken from './account/company-form-token'
import InfoAlertBox from "./info-alert-box.vue"

export default {
    mixins: [
        company_token_mixin
    ],

    components: {
        InfoAlertBox,
        CompanyFormToken
    },

    data() {
        return {
            statics: {
                logo: null,
                logo_inverse: null,
                logo_square: null,
                logo_square_inverse: null,
                host: null,
                referer: null,
                name: null,
                domain: null,
                whitelabel: false,
                path: null
            },
            loading_whitelabel: true
        }
    },

    created() {
        this.getStatics()
    },

    methods: {
        getStatics() {
            this.loading_whitelabel = true
            axios.get('/get-statics')
                .then(res => {
                    this.statics = res.data
                    this.loading_whitelabel = false
                })
                .catch(err => {
                    console.log(err)
                    this.$root.handleErrors(err.response)
                    this.loading_whitelabel = false
                })
        }
    }
}
</script>
